import React from 'react';

import PropTypes from 'prop-types';
import cn from 'classnames';
import { FaUpload } from 'react-icons/fa';
import uploadcare from 'uploadcare-widget';
import { useDropzone } from 'react-dropzone';

function FormInputUploader({
  isMultiple,
  maxSize,
  isImageOnly,
  fileType,
  caption,
  isUploadcare,
  onChange,
  settings,
  required,
  name,
  label,
  onSetFieldValue,
  returnObject,
  crop,
  iconClassName,
  containerClassName,
  setIsLoading,
  error,
  errorClassName,
}) {
  const [fileUploadError, setFileUploadError] = React.useState(false);
  const [isUploading, setIsUploading] = React.useState(false);
  const [progressCount, setProgressCount] = React.useState(0);

  const returnType = React.useCallback(
    (x) => {
      if (returnObject) {
        return x;
      }
      return x?.cdnUrl;
    },
    [returnObject]
  );

  const handleProgress = ({ state, progress }) => {
    if (state === 'uploading') {
      setIsUploading(true);
      setProgressCount(Math.ceil(progress * 100));
    }
    if (state === 'uploaded') {
      setIsUploading(true);
      setProgressCount(100);
    }

    if (state === 'ready') {
      setIsUploading(false);
      setProgressCount(0);
    }
  };

  const handleDoneMultipleFiles = React.useCallback(
    async (data) => {
      const newData = await data.files();

      const process = (item) =>
        new Promise((r) => {
          item.done((file) => {
            r(file);
          });
        });

      const rawFiles = await Promise.all(newData.map((i) => process(i)));

      if (typeof onSetFieldValue === 'function') {
        onSetFieldValue(
          name,
          rawFiles?.map((v) => returnType(v))
        );
      }

      if (typeof onChange === 'function') {
        onChange((prev) => ({
          ...prev,
          [name]: rawFiles?.map((v) => returnType(v)),
        }));
      }
    },
    [name, onChange, onSetFieldValue, returnType]
  );

  const handleDone = React.useCallback(
    (data) => {
      if (typeof onSetFieldValue === 'function') {
        onSetFieldValue(name, returnType(data));
      }

      if (typeof onChange === 'function') {
        onChange((prev) => ({ ...prev, [name]: returnType(data) }));
      }
    },
    [name, onChange, onSetFieldValue, returnType]
  );

  const uploadingFile = React.useCallback(
    async (data) => {
      if (isMultiple) {
        const multipleProgress = data.promise();
        await multipleProgress.progress(handleProgress);
        handleDoneMultipleFiles(data);
        return;
      }
      const singleProgress = data.promise();
      await singleProgress.progress(handleProgress);
      data.done(handleDone);
    },
    [handleDone, handleDoneMultipleFiles, isMultiple]
  );

  const uploadByUpladCare = React.useCallback(
    (files) => {
      try {
        uploadcare
          .openDialog(uploadcare.filesFrom('object', files), {
            publicKey: process.env.REACT_APP_UPLOADCARE_KEY,
            imageOnly: isImageOnly,
            tabs: 'file url',
            effects: 'crop,rotate,mirror',
            crop,
            previewStep: true,
            multiple: isMultiple,
            ...settings,
          })
          .done(uploadingFile);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log('File size is too large');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isMultiple, uploadingFile, isImageOnly]
  );

  const onDrop = React.useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const errorMessage = isMultiple
          ? 'Some file is to large to upload or file format is invalid.'
          : 'File is to large to upload or invalid format.';
        setFileUploadError(errorMessage);
        return;
      }
      if (!isMultiple && acceptedFiles.length > 1) {
        setFileUploadError('You uploading file exceeded system limit.');
        return;
      }

      if (isUploadcare) {
        uploadByUpladCare(acceptedFiles);
        return;
      }
      // eslint-disable-next-line no-console
      console.log('Uploading is compromise.');
    },
    [isMultiple, isUploadcare, uploadByUpladCare]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxSize: maxSize * 1048576,
  });

  React.useEffect(() => {
    if (!setIsLoading) return;

    setIsLoading(isUploading);
  }, [isUploading, setIsLoading]);

  return (
    <div className="relative w-full h-full">
      {label && (
        <h4 className="form-label">
          {label} {required && <span className="text-red-500">*</span>}
        </h4>
      )}
      <div className="relative w-full h-full">
        <div className="text-center flex w-full h-full cursor-pointer bg-white">
          <div
            {...getRootProps()}
            className={cn(
              'flex-1 flex justify-center items-center transition duration-300 ease-in-out outline-none border-2 focus-within:border-gray-400 border-dashed group border-gray-200 hover:border-gray-400 p-5 h-full rounded',
              containerClassName
            )}
          >
            <div className="space-y-5 md:space-y-3">
              <div className="w-full flex justify-center text-gray-800">
                <FaUpload className={cn('w-10 h-10', iconClassName)} />
              </div>
              <input {...getInputProps()} accept={fileType} />
              <div>
                {!fileUploadError ? (
                  <div>
                    {isDragActive ? (
                      'Drop your files here'
                    ) : (
                      <>
                        <p className="font-semibold text-sm">
                          <span className="text-primary-500">
                            Upload a file{' '}
                          </span>
                          or drag and drop
                        </p>
                        <p className="text-sm">{caption}</p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="mt-2">
                    <p className="font-semibold text-sm">
                      <span className="text-primary-500">Upload a file </span>
                      or drag and drop
                    </p>
                    <div className="font-semibold text-sm text-red-600">
                      {fileUploadError}
                    </div>
                  </div>
                )}
              </div>
              {error && (
                <small className={cn('form-error-badge', errorClassName)}>
                  {error ?? ''}
                </small>
              )}
            </div>
            {isUploading && (
              <div className="mt-10 text-xs bg-opacity-50 bg-black px-4 py-2 rounded text-left absolute z-10 left-1 right-1 bottom-1">
                <div className="flex items-center text-white">
                  <div className="font-semibold">Uploading...</div>
                  <div className="ml-auto">{progressCount}%</div>
                </div>
                <div className="bg-white mt-1 rounded-full">
                  <div
                    style={{
                      width: `${progressCount}%`,
                    }}
                    className="percentage h-1 bg-primary-500 rounded-full"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

FormInputUploader.defaultProps = {
  maxSize: 10,
  isImageOnly: true,
  fileType: 'image/*',
  label: false,
  required: false,
  isUploadcare: true,
  caption: 'PNG, JPG up to 10mb',
  settings: {},
  crop: 'free, 4:4',
  isMultiple: false,
  onChange: false,
  returnObject: false,
  onSetFieldValue: false,
  iconClassName: '',
  containerClassName: '',
  error: '',
  errorClassName: '',
  setIsLoading: false,
};

FormInputUploader.propTypes = {
  label: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.element,
    PropTypes.bool,
  ]),
  required: PropTypes.bool,
  isImageOnly: PropTypes.bool,
  isUploadcare: PropTypes.bool,
  fileType: PropTypes.string,
  maxSize: PropTypes.number,
  caption: PropTypes.string,
  crop: PropTypes.string,
  iconClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  returnObject: PropTypes.bool,
  onChange: PropTypes.oneOfType([
    PropTypes.instanceOf(Function),
    PropTypes.bool,
  ]),
  onSetFieldValue: PropTypes.oneOfType([
    PropTypes.instanceOf(Function),
    PropTypes.bool,
  ]),
  setIsLoading: PropTypes.oneOfType([
    PropTypes.instanceOf(Function),
    PropTypes.bool,
  ]),
  settings: PropTypes.instanceOf(Object),
  isMultiple: PropTypes.bool,
  error: PropTypes.string,
  name: PropTypes.string.isRequired,
  errorClassName: PropTypes.string,
};

export default FormInputUploader;
